/**
 * Organization Layout Component
 *
 * Provides shared layout for organization routes with navigation tabs.
 * Uses React Router's Outlet for nested route rendering.
 */

import type { JSX } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { usePermission } from '~/hooks/usePermission';
import { useTenant } from '~/hooks/useTenant';

import { LoadingSpinner } from '~/components/LoadingSpinner';

interface NavItem {
  to: string;
  label: string;
  danger?: boolean;
}

export function Organization(): JSX.Element {
  const { currentOrg, loading } = useTenant();
  const { canDeleteOrg, canManageDomains, canManageMembers } = usePermission();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <p>No organization found</p>
      </div>
    );
  }

  const navItems: NavItem[] = [
    { to: '/org/settings', label: 'Settings' },
    ...(canManageMembers ? [{ to: '/org/members', label: 'Members' }] : []),
    ...(canManageMembers ? [{ to: '/org/teams', label: 'Teams' }] : []),
    ...(canManageDomains ? [{ to: '/org/domains', label: 'Domains' }] : []),
    ...(canDeleteOrg ? [{ to: '/org/danger-zone', label: 'Danger Zone', danger: true }] : []),
  ];

  const getActiveClass = (path: string): string => {
    const isActive =
      location.pathname === path ||
      (path === '/org/teams' && location.pathname.startsWith('/org/teams/'));

    return isActive
      ? 'border-primary text-foreground'
      : 'border-transparent text-foreground/60 hover:text-foreground';
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-4">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Organization</h1>
        <p>{currentOrg.name}</p>
      </div>

      {/* Navigation Tabs */}
      <nav className="border-b border-default mb-4" aria-label="Organization">
        <div className="flex gap-6">
          {navItems.map(({ danger, label, to }) => (
            <NavLink
              key={to}
              to={to}
              className={`px-0 h-12 flex items-center border-b-2 font-medium text-sm transition-colors ${
                danger ? 'text-danger hover:text-danger' : ''
              } ${getActiveClass(to)}`}
            >
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      <Outlet />
    </div>
  );
}
